// /admin/map — staff-only map view of the platform. Three layers:
//   * Communities — centroid pins + polygon boundaries when drawn
//   * Places      — pinned businesses
//   * Heatmap     — user home cells (N≥10 minimum, server-side enforced)
//
// Interim: uses the free Protomaps demo tileset. Swap to our own
// tiles.onhere.cc URL once the in-cluster nginx pod ships (loc-4).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import { type LngLatLike, type Map as MLMap, Popup, NavigationControl, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useApi } from '../lib/api';

// Interim: OSM raster tiles with a CSS grayscale filter for the newsprint
// vibe. No PMTiles registration required — works out of the box, no CORS
// dance, no protocol quirks. Swap to `pmtiles://https://tiles.onhere.cc/
// tiles/za.pmtiles` + newsprint.json style once loc-4 ships the in-cluster
// nginx pod (see docs/location-awareness.md).
const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

// Vredehoek launch community — default boot view. Zoom 14 gets the
// suburb + City Bowl edges in a comfortable single view.
const CAPE_TOWN: LngLatLike = [18.4152, -33.9376];

interface CommunityMarker {
    id: string; slug: string; name: string;
    lat?: number | null; lon?: number | null;
    boundary?: GeoJSON.Polygon | null;
}
interface PlaceMarker {
    id: string; slug: string; name: string; category: string; address: string;
    lat: number; lon: number;
    website?: string | null; phone?: string | null;
    group_id?: string | null; group_slug?: string | null;
    claim_status: 'unclaimed' | 'pending' | 'claimed' | 'disputed';
}
interface HeatCell {
    lat: number; lon: number; count: number;
}

export function AdminMap() {
    const api = useApi();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef       = useRef<MLMap | null>(null);
    const [showCommunities, setShowCommunities] = useState(true);
    // 'all' | 'claimed' | 'unclaimed' | 'off' — segmented filter for the
    // places layer. Claimed = green pins, Unclaimed = grey pins, All
    // shows both, Off hides the layer entirely.
    const [placesFilter,    setPlacesFilter]    = useState<'all'|'claimed'|'unclaimed'|'off'>('all');
    const [showHeatmap,     setShowHeatmap]     = useState(true);
    const [status, setStatus] = useState('');
    // ready flips true after the map fires 'load' — every layer render
    // gates on this so we never try to addSource before the style is up.
    const [ready, setReady] = useState(false);

    // Boot the map once.
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        try {
            const m = new maplibregl.Map({
                container: containerRef.current,
                style: minimalNewsprintStyle(OSM_TILE_URL),
                center: CAPE_TOWN,
                zoom: 14,
            });
            m.addControl(new NavigationControl(), 'top-right');
            // Three shots to flip ready. Whichever fires first wins.
            // 'style.load' is the most reliable — raster tile downloads
            // shouldn't gate our overlay rendering.
            m.on('style.load', () => setReady(true));
            m.on('load',       () => setReady(true));
            setTimeout(() => setReady(true), 1500);
            mapRef.current = m;
        } catch (e) {
            setStatus('Map init failed: ' + (e as Error).message);
        }
        return () => { mapRef.current?.remove(); mapRef.current = null; };
    }, []);

    // Reload data layers on mount + when toggles flip.
    const load = useCallback(async () => {
        const map = mapRef.current;
        if (!map) { setStatus('load: no map ref'); return; }
        setStatus('loading…');
        try {
            const [c, p, h] = await Promise.all([
                api.get<{ communities: CommunityMarker[] }>('/api/admin/map/communities'),
                api.get<{ places:      PlaceMarker[] }>('/api/admin/map/places'),
                api.get<{ cells:       HeatCell[]; min_per_cell: number }>('/api/admin/map/heatmap'),
            ]);
            const communities = c.communities ?? [];
            const places      = p.places      ?? [];
            const cells       = h.cells       ?? [];
            // Apply the segmented places filter client-side. The endpoint
            // always returns everything; SPA decides what to render.
            const filteredPlaces = placesFilter === 'off'
                ? []
                : placesFilter === 'claimed'
                    ? places.filter(p => p.claim_status === 'claimed')
                    : placesFilter === 'unclaimed'
                        ? places.filter(p => p.claim_status === 'unclaimed')
                        : places;
            const claimedN   = places.filter(p => p.claim_status === 'claimed').length;
            const unclaimedN = places.filter(p => p.claim_status === 'unclaimed').length;
            console.log('[admin-map] fetched', { communities: communities.length, places: places.length, showing: filteredPlaces.length, cells: cells.length });
            try { renderCommunityLayer(map, showCommunities ? communities : []); }
            catch (e) { console.error('community layer', e); }
            try { renderPlaceLayer   (map, filteredPlaces); }
            catch (e) { console.error('place layer', e); }
            try { renderHeatmap      (map, showHeatmap ? cells : []); }
            catch (e) { console.error('heatmap layer', e); }
            setStatus(`${communities.length} communities · ${claimedN} claimed / ${unclaimedN} unclaimed · ${cells.length} cells (N≥${h.min_per_cell})`);
        } catch (e) {
            const msg = 'Data load failed: ' + (e as Error).message;
            console.error(msg);
            setStatus(msg);
        }
    }, [api, showCommunities, placesFilter, showHeatmap]);

    // Kick load once the map fires 'load' AND whenever the layer toggles
    // flip. The `ready` gate prevents the first-render addSource from
    // racing the style-load.
    useEffect(() => {
        if (ready) load();
    }, [ready, load]);

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1400, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontFamily: 'Newsreader,serif', fontSize: 28 }}>Map</h1>
            </div>
            <nav style={adminNav}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
                <label style={cbl}><input type="checkbox" checked={showCommunities} onChange={e => setShowCommunities(e.target.checked)} /> Communities</label>
                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>Places</span>
                    {(['all','claimed','unclaimed','off'] as const).map(f => (
                        <button key={f} type="button" onClick={() => setPlacesFilter(f)}
                                style={segBtn(placesFilter === f)}>
                            {f}
                        </button>
                    ))}
                </span>
                <label style={cbl}><input type="checkbox" checked={showHeatmap}     onChange={e => setShowHeatmap(e.target.checked)} /> Reader heatmap</label>
                <span style={{ color: '#666', marginLeft: 'auto' }}>{status}</span>
            </div>
            <div ref={containerRef}
                 className="newsprint-map"
                 style={{ width: '100%', height: 'calc(100vh - 200px)', minHeight: 500, border: '1px solid var(--ink)' }} />
            <p style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                Interim tileset from Protomaps. Reader heatmap enforces N≥10 per cell server-side —
                no individual pins render here (see docs/location-awareness.md).
            </p>
        </main>
    );
}

// -----------------------------------------------------------------------
// Style — minimal newsprint vibe. Loc-5 will replace this with the full
// hand-tuned JSON; this gets us on-screen fast.

function minimalNewsprintStyle(tilesURL: string): maplibregl.StyleSpecification {
    // Simple raster style — one OSM source, one raster layer. Newsprint
    // vibe comes from a CSS grayscale + sepia filter on the canvas below.
    return {
        version: 8,
        sources: {
            osm: {
                type: 'raster',
                tiles: [tilesURL],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors',
            },
        },
        layers: [
            { id: 'bg', type: 'background', paint: { 'background-color': '#fdfcf6' } },
            { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-opacity': 0.85 } },
        ],
    } as maplibregl.StyleSpecification;
}

// -----------------------------------------------------------------------
// Layer renderers — each is idempotent: removes prior source/layer then adds.

function renderCommunityLayer(map: MLMap, items: CommunityMarker[]) {
    removeIfExists(map, ['comm-fill', 'comm-line', 'comm-points']);
    if (map.getSource('communities')) map.removeSource('communities');

    const boundariesFC: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: items.filter(c => c.boundary).map(c => ({
            type: 'Feature', geometry: c.boundary!, properties: { name: c.name, slug: c.slug, id: c.id },
        })),
    };
    const centroidsFC: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: items.filter(c => c.lat != null && c.lon != null).map(c => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [c.lon!, c.lat!] },
            properties: { name: c.name, slug: c.slug, id: c.id },
        })),
    };

    map.addSource('communities', { type: 'geojson', data: boundariesFC });
    map.addLayer({ id: 'comm-fill', type: 'fill', source: 'communities',
        paint: { 'fill-color': '#2b5e3f', 'fill-opacity': 0.05 } });
    map.addLayer({ id: 'comm-line', type: 'line', source: 'communities',
        paint: { 'line-color': '#111', 'line-width': 1.5, 'line-dasharray': [2, 2] } });

    if (!map.getSource('community-centroids')) {
        map.addSource('community-centroids', { type: 'geojson', data: centroidsFC });
        map.addLayer({ id: 'comm-points', type: 'circle', source: 'community-centroids',
            paint: { 'circle-radius': 5, 'circle-color': '#2b5e3f', 'circle-stroke-color': '#fdfcf6', 'circle-stroke-width': 1.5 } });
    } else {
        (map.getSource('community-centroids') as maplibregl.GeoJSONSource).setData(centroidsFC);
    }

    map.on('click', 'comm-points', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties as { name: string; slug: string; id: string };
        new Popup().setLngLat(e.lngLat)
            .setHTML(`<strong>${escapeHTML(p.name)}</strong><br><a href="/community/${escapeHTML(p.slug)}">Open community →</a>`)
            .addTo(map);
    });
}

// Track DOM markers so we can clear + re-add on re-render. MapLibre's
// Marker class renders a real DOM element outside the WebGL canvas, so
// it's immune to whatever's stopping the circle-layer paint. Using it as
// the primary render for now; the circle-layer path can come back once we
// switch to the newsprint PMTiles style.
const placeMarkers: Marker[] = [];

function renderPlaceLayer(map: MLMap, items: PlaceMarker[]) {
    console.warn('[admin-map] renderPlaceLayer start, items:', items.length);
    // Clear prior markers.
    while (placeMarkers.length) placeMarkers.pop()!.remove();

    for (const p of items) {
        const el = document.createElement('div');
        // Match /places colour convention — green claimed, grey unclaimed.
        // Disputed = orange so staff can spot them at a glance.
        const bg = p.claim_status === 'unclaimed' ? '#a1a1a1'
                 : p.claim_status === 'disputed'  ? '#e5a24b'
                 : p.claim_status === 'pending'   ? '#dcb85a'
                 : '#2b5e3f';
        el.style.cssText = [
            'width: 20px', 'height: 20px', 'border-radius: 50%',
            `background: ${bg}`, 'border: 2px solid #fff',
            'box-shadow: 0 2px 6px rgba(0,0,0,0.35)', 'cursor: pointer',
        ].join(';');
        el.title = p.name;

        const html = `
            <div style="font-family: 'Newsreader',serif; min-width: 180px;">
                <div style="font-weight:700; font-size:15px;">${escapeHTML(p.name)}</div>
                <div style="font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:#666;">${escapeHTML(p.category)}</div>
                <div style="font-size:12px; margin-top:4px; color:#333;">${escapeHTML(p.address)}</div>
                ${p.group_slug ? '<div style="font-size:11px; margin-top:6px; color:#888;">Click pin to open →</div>' : ''}
            </div>`;
        const popup = new Popup({ closeButton: false, offset: 14 }).setHTML(html);

        // Show popup on hover, hide when leaving the pin.
        el.addEventListener('mouseenter', () => popup.setLngLat([p.lon, p.lat]).addTo(map));
        el.addEventListener('mouseleave', () => popup.remove());
        el.addEventListener('click', () => {
            if (p.group_slug) window.location.href = '/groups/' + p.group_slug + '/dashboard';
        });

        const m = new Marker({ element: el }).setLngLat([p.lon, p.lat]).addTo(map);
        placeMarkers.push(m);
    }
    console.warn('[admin-map] renderPlaceLayer done, markers:', placeMarkers.length);
}

function renderHeatmap(map: MLMap, cells: HeatCell[]) {
    removeIfExists(map, ['heatmap-layer']);
    if (map.getSource('heatmap')) map.removeSource('heatmap');
    if (cells.length === 0) return;

    const fc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: cells.map(c => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
            properties: { count: c.count },
        })),
    };
    map.addSource('heatmap', { type: 'geojson', data: fc });
    map.addLayer({ id: 'heatmap-layer', type: 'heatmap', source: 'heatmap',
        paint: {
            'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, 100, 1],
            'heatmap-intensity': 1,
            'heatmap-radius': 30,
            'heatmap-opacity': 0.55,
        } });
}

function removeIfExists(map: MLMap, layerIDs: string[]) {
    for (const id of layerIDs) if (map.getLayer(id)) map.removeLayer(id);
}
function escapeHTML(s: string) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ---- styles ----------
const adminNav: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7', fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLink: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};
const cbl: React.CSSProperties = { display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' };
function segBtn(active: boolean): React.CSSProperties {
    return {
        padding: '2px 8px', fontSize: 11, cursor: 'pointer',
        border: '1px solid ' + (active ? 'var(--ink)' : '#ccc'),
        background: active ? 'var(--ink)' : '#fff',
        color: active ? '#fff' : '#333',
        fontFamily: '-apple-system, sans-serif',
        textTransform: 'lowercase', letterSpacing: 0.5,
    };
}
